import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { RichDataTable } from "../../../../components/table/RichDataTable";
import { Column } from "primereact/column";
import {
  PageHeight40,
  PageTitle,
  createSortByAttr,
  scrollToTop,
  toUIDate,
} from "../../../../components/Utils";
import { FormMenu } from "../../../../components/Buttons";
import ItemListManager from "../../../../components/ItemListManager";
import ANPConnectionTaskForm from "../../components/itConnections/ANPConnectionTaskForm";
import ANPConnectionTaskMenu from "../../components/itConnections/ANPConnectionTaskMenu";

import { connect } from "react-redux";
import {
  setSelectedANPConnections,
  exportANPConnections,
  filterANPConnections,
  closeConnectionTask,
  closeConnectionTasks,
  deleteConnectionTask,
} from "../../actions";
import {
  columnStyle,
  filterTasksByTaskType,
  getTaskTypeLabel,
  isDeletionTask,
  isUpdateTask,
  renderNgumUserIDColumn,
  renderRequesterColumn,
  renderUserNameColumn,
  actionColumnStyle,
} from "../../components/Utils";

const CONNECTION_TYPE = "ANP";

const mapStateToProps = (state) => {
  return {
    isLoading: state.task.loading,
    data: state.task.anpConnections,
    selection: state.task.selectedANPConnections,
    connectionTaskType: state.task.currentANPConnectionType,
  };
};
const mapDispatchToProps = (dispatch) => ({
  setSelection: (...args) => dispatch(setSelectedANPConnections(...args)),
  exportANPConnections: (...args) => dispatch(exportANPConnections(...args)),
  filterConnections: (...args) => dispatch(filterANPConnections(...args)),
  closeTask: (...args) => dispatch(closeConnectionTask(...args)),
  deleteTask: (...args) => dispatch(deleteConnectionTask(...args)),
  closeTaskList: (...args) => dispatch(closeConnectionTasks(...args)),
});

const ANPConnectionTaskList = ({
  className = "",
  rows = 10,
  isLoading,
  data,
  selection,
  setSelection,
  exportANPConnections,
  filterConnections,
  connectionTaskType,
  closeTask,
  deleteTask,
  closeTaskList,
}) => {
  const formRef = useRef(null);

  useEffect(() => {
    scrollToTop();
  }, []);

  const onSave = useCallback(
    (onHide) => {
      const task = formRef.current.getModel();
      if (!task) return;
      console.log(`>> saving ${CONNECTION_TYPE} connection task...`, task);
      closeTask({ connectionType: CONNECTION_TYPE, task }, onHide);
    },
    [closeTask],
  );

  const onDelete = useCallback(
    (task) => {
      console.log(`${CONNECTION_TYPE}ConnectionTaskList.onDelete()`, task);
      deleteTask({ connectionType: CONNECTION_TYPE, task });
    },
    [deleteTask],
  );

  const onCloseTasks = useCallback(
    (tasks) => {
      console.log("closing tasks", tasks);
      closeTaskList({ connectionType: CONNECTION_TYPE, tasks });
    },
    [closeTaskList],
  );

  console.log(
    "ANPConnectionTaskList.render() connectionTaskType:",
    connectionTaskType,
  );

  const isUpdating = useMemo(
    () => isUpdateTask(connectionTaskType),
    [connectionTaskType],
  );
  const isDeleting = useMemo(
    () => isDeletionTask(connectionTaskType),
    [connectionTaskType],
  );

  const renderItemTable = useCallback(
    (items, actionTemplate) => {
      const taskItems = items
        .map((item) => ({
          ...item,
          user: (item.user || "").split(",").join(" "),
          requester: `${item.requester || ""}`.split(",").join(" "),
          requestDateUI: toUIDate(
            item.request_date,
            "YYYY-MM-DDTHH:mm:ss:SSSZ",
          ),
          requestDate: toUIDate(
            item.request_date,
            "YYYY-MM-DDTHH:mm:ss:SSSZ",
            "YYYY.MM.DD",
          ),
          userInCharge: `${item.userInCharge || ""}`.split(",").join(" "),
        }))
        .sort(createSortByAttr("user"));
      return (
        <RichDataTable
          paginator
          paginatorLeftProps={{
            emptyText: "Keine AN-Portal Zugänge",
            itemName: "AN-Portal Zugang",
            itemNamePlural: "AN-Portal Zugänge",
          }}
          data={taskItems}
          rows={rows}
          selection={selection}
          setSelection={setSelection}
          removableSort
          sortMode="multiple"
          isLoading={isLoading}
          connectionTaskType={connectionTaskType}
        >
          <Column selectionMode="multiple" style={{ width: "3em" }} />
          <Column
            field="parentCompanyName"
            header="Vertragspartner"
            sortable={taskItems.length > 1}
            filter // ={taskItems.length > rows}
            filterMatchMode="contains"
            style={{ ...columnStyle }}
          />
          <Column
            field="user"
            header="Mitarbeiter"
            sortable={taskItems.length > 1}
            filter // ={taskItems.length > rows}
            filterMatchMode="contains"
            style={{ ...columnStyle }}
            body={renderUserNameColumn}
          />
          <Column
            field="ngumUserId"
            header="nGUM UserID"
            sortable={taskItems.length > 1}
            filter // ={taskItems.length > rows}
            filterMatchMode="contains"
            style={{ ...columnStyle }}
            body={renderNgumUserIDColumn}
          />
          <Column
            field="requester"
            header="Beantrager"
            sortable={taskItems.length > 1}
            filter // ={taskItems.length > rows}
            filterMatchMode="contains"
            style={{ ...columnStyle, width: "12em" }}
            body={renderRequesterColumn}
          />
          <Column
            field="requestDate"
            header="Erstelldatum"
            sortable={taskItems.length > 1}
            filter // ={taskItems.length > rows}
            filterMatchMode="contains"
            style={{ ...columnStyle, width: "10em", textAlign: "center" }}
            body={(item) => item.requestDateUI}
          />
          <Column
            field="status"
            header="Status"
            sortable={taskItems.length > 1}
            filter // ={taskItems.length > rows}
            filterMatchMode="contains"
            style={{ ...columnStyle, width: "9em" }}
          />
          <Column
            body={actionTemplate}
            style={{ ...actionColumnStyle, textAlign: "center", width: "7em" }}
          />
        </RichDataTable>
      );
    },
    [rows, selection, setSelection, isLoading, connectionTaskType],
  );

  const renderItemForm = useCallback(
    (item, onHide) => {
      const connection = item;
      return (
        <div>
          <ANPConnectionTaskForm
            ref={formRef}
            model={connection}
            isUpdating={isUpdating}
            isDeleting={isDeleting}
          />
          <FormMenu
            className="margin-big-top w3-border-top pad-big"
            style={{
              marginLeft: "-1em",
              marginRight: "-1em",
              marginBottom: "-1em",
            }}
            onSave={() => onSave(onHide)}
            onCancel={onHide}
            saveBtnName="saveANPConnectionBtn"
            saveBtnLabel={isDeleting ? "Bestätigen" : undefined}
          />
        </div>
      );
    },
    [isDeleting, isUpdating, onSave],
  );

  const getItemFormTitle = (/* connection */) => {
    let title = "AN-Portal Zugang bearbeiten";
    if (isDeleting) {
      title = "AN-Portal Zugängelöschung bestätigen";
    }
    return <h3 className="no-margin">{title}</h3>;
  };

  const title = (
    <PageTitle>
      <ANPConnectionTaskMenu
        className="w3-right"
        selectedANPConnections={selection}
        exportANPConnections={exportANPConnections}
        closeANPConnections={onCloseTasks}
        filterConnections={filterConnections}
        connectionTaskType={connectionTaskType}
        items={data}
      />
      <h3 className="no-margin">{"AN-Portal Zugänge"}</h3>
    </PageTitle>
  );

  return (
    <PageHeight40 className={className}>
      <ItemListManager
        key={Math.random()}
        title={title}
        itemPanelStyle={{ width: "40rem" }}
        addBtnHidden
        items={data.filter((item) =>
          filterTasksByTaskType(item, connectionTaskType),
        )}
        getItemFormTitle={getItemFormTitle}
        renderItemTable={renderItemTable}
        renderItemForm={renderItemForm}
        namePrefix={`${CONNECTION_TYPE}Connection`}
        itemDeleteable
        deleteTitle={`AN-Portal Zugangsaufgabe löschen`}
        getDeleteText={(_item) => {
          const taskLabel = getTaskTypeLabel(connectionTaskType);
          return `Diese ${taskLabel} wird nun rückgängig gemacht. Wollen Sie diese Aufgabe unwiderruflich löschen?`;
        }}
        onDelete={onDelete}
      />
    </PageHeight40>
  );
}

ANPConnectionTaskList.propTypes = {
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  data: PropTypes.array,
  selection: PropTypes.array,
  connectionTaskType: PropTypes.string,
  exportANPConnections: PropTypes.func.isRequired,
  filterConnections: PropTypes.func.isRequired,
  closeTask: PropTypes.func.isRequired,
  closeTaskList: PropTypes.func.isRequired,
  rows: PropTypes.number,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ANPConnectionTaskList);
